import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  delay,
  distinctUntilChanged,
  take,
  Subject,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { isPresent, LocalDate, unsubscribe, doLater } from '@fcom/core/utils';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  DateLabels,
  DateRange,
  LoaderTheme,
  PopoverOptions,
  PopoverPosition,
  UiLabels,
} from '@fcom/ui-components';
import { Amount } from '@fcom/dapi';
import { LanguageService } from '@fcom/ui-translate';
import { LocationRouteCffService } from '@fcom/core';
import { TripType } from '@fcom/core/interfaces';
import {
  Direction,
  ElementTypes,
  FlightSegment,
  GlobalBookingTripDates,
  PriceCalendarParams,
  PriceCalendarWithPricesParams,
} from '@fcom/common/interfaces';
import { ElementActions, GaContext, SearchType } from '@fcom/common/interfaces/gtm.interface';
import { BOOKING_WIDGET_CONTEXT } from '@fcom/booking-widget/constants';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';

import { DatePickerPrices, HistogramBar, PriceCalendarCTAParams } from '../../interfaces';
import { DAYS_MONTH, MediaQueryService, PriceCalendarService } from '../../services';
import {
  calendarDatesToGlobalBookingTripDates,
  priceCalendarBaseParamsAreValid,
  shouldUpdateCalendarDates,
} from '../../utils';
import { GtmService } from '../../gtm';

export enum PriceCalendarType {
  PRICES = 'prices',
  ONLY_DATES = 'onlyDates',
}

@Component({
  selector: 'fin-price-calendar',
  templateUrl: 'price-calendar.component.html',
  styleUrls: ['./price-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PriceCalendarComponent implements OnInit, OnDestroy {
  private priceCalendarService = inject(PriceCalendarService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private languageService = inject(LanguageService);
  private gtmService = inject(GtmService);
  private mediaQuerySerivce = inject(MediaQueryService);

  readonly datepickerLabels$ = this.languageService.translate<UiLabels>('datepicker');
  readonly dateLabels$ = this.languageService.translate<DateLabels>('date');

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly LoaderTheme = LoaderTheme;
  readonly CMS_STYLE_OVERRIDE = {
    'margin-top': '0px',
  };
  readonly PriceCalendarType = PriceCalendarType;
  readonly CmsTripTypeMap = CmsTripTypeMap;

  @Input()
  type: PriceCalendarType = PriceCalendarType.PRICES;

  @Input()
  openWithParams$: Observable<PriceCalendarParams | null>;

  @Input()
  showAddReturn = false;

  @Input()
  identifier: string;

  @Input()
  disabledDateRanges$: Observable<DateRange[]> = of([]);

  @Input()
  showFlexibleDatesSelection = false;

  @Input()
  showTripPriceFrom = true;

  @Input()
  showSubtitle = false;

  @Input()
  ctaLabel = '';

  @Input()
  enablePreFlightSearchTracking = true;

  @Input()
  enableNewSearchAutomatically = false;

  @Input()
  isSearchEnabled = false;

  @Output()
  addReturnClicked = new EventEmitter();

  @Output()
  modalClose = new EventEmitter<boolean>();

  @Output()
  ctaClicked = new EventEmitter<PriceCalendarCTAParams>();

  @Output()
  travelDatesChanged = new EventEmitter<GlobalBookingTripDates>();

  @Output()
  startSearch = new EventEmitter<void>();

  @Output()
  cancelSelection = new EventEmitter<void>();

  subscription = new Subscription();

  calendarRange: DateRange = [LocalDate.now(), LocalDate.now().plusDays(360)];
  pricePerAdult$: Observable<Amount> = of(undefined);
  datesSelected$: Observable<boolean> = of(false);
  datePickerTitleLabel$: Observable<string> = of('');
  subTitle$: Observable<{ origin: string; destination: string }>;
  calendarPrices$: Observable<DatePickerPrices>;
  tripType$: Observable<TripType> = of(undefined);
  travelDates$: Observable<GlobalBookingTripDates> = of(undefined);
  flexibleDates$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  flexibleDatesCtrl = new FormControl<boolean>(false, { nonNullable: true });
  flexibleDatesForm = new FormGroup({ flexibleDates: this.flexibleDatesCtrl });
  ctaDisabled$: Observable<boolean> = of(false);
  isLoading$: Observable<boolean> = of(true);
  selectedHistogramMonth$: BehaviorSubject<number> = new BehaviorSubject(undefined);
  scrollToHistogramMonth$: BehaviorSubject<number> = new BehaviorSubject(undefined);
  renderDatepicker$ = new BehaviorSubject(false);
  noPricesAvailableForDates$: Observable<boolean> = of(false);
  pricesAvailable$: Observable<boolean> = of(false);
  isMobile$: Observable<boolean> = of(false);
  enablePriceSearchPopover$: Observable<boolean>;
  firstDateToShowPriceSearchPopover$: Observable<LocalDate> = of(undefined);
  preFlightSearchTrigger$ = new Subject<boolean>();

  readonly popoverOptions: PopoverOptions = {
    context: BOOKING_WIDGET_CONTEXT,
    enableOnHover: true,
    zIndex: 10,
    popoverPosition: PopoverPosition.BOTTOM,
    popoverWidth: 180,
    roundedCorners: true,
    showArrowCaret: true,
    useFixedPosition: false,
    showLeftBorder: false,
    hasPadding: true,
    shadow: true,
  };
  hasDiscountCode$: Observable<boolean>;

  ngOnInit(): void {
    this.subscription.add(this.flexibleDatesCtrl.valueChanges.subscribe((v) => this.flexibleDates$.next(v)));
    this.subscription.add(
      this.openWithParams$.pipe(delay(1)).subscribe((params) => {
        this.renderDatepicker$.next(params !== null);
      })
    );
    this.startSearchWithSelectedDates();
    this.isLoading$ = this.type === PriceCalendarType.PRICES ? this.priceCalendarService.isLoading$ : of(false);
    this.travelDates$ = this.priceCalendarService.travelDates$;
    this.tripType$ = this.priceCalendarService.tripType$;
    this.isMobile$ = this.mediaQuerySerivce.isBreakpoint$('tablet_down');

    if (this.type === PriceCalendarType.PRICES) {
      this.calendarPrices$ = this.priceCalendarService.calendarPrices$;
      this.pricePerAdult$ = this.priceCalendarService.startingFromPrice$;
    }

    const hasRequiredDates$ = combineLatest([
      this.priceCalendarService.travelDates$,
      this.priceCalendarService.tripType$,
    ]).pipe(
      map(([travelDates, tripType]) => {
        return (
          (tripType === TripType.RETURN && isPresent(travelDates.returnDate)) ||
          (tripType === TripType.ONEWAY && isPresent(travelDates.departureDate))
        );
      })
    );

    const hasConsecutiveFollowingMonthPrices$ = combineLatest([
      this.priceCalendarService.followingMonthPrices$,
      this.travelDates$,
      this.tripType$,
    ]).pipe(
      filter(
        ([followingMonthPrices, travelDates, tripType]) =>
          isPresent(followingMonthPrices) && isPresent(travelDates?.departureDate) && tripType === TripType.RETURN
      ),
      map(([followingMonthPrices, { departureDate }]) =>
        Object.entries(followingMonthPrices).every(
          ([date, value]) => date === departureDate.id || (isPresent(value) && isPresent(value.amount))
        )
      )
    );

    this.enablePriceSearchPopover$ = combineLatest([
      this.tripType$,
      this.priceCalendarService.pricesAvailable$,
      this.isMobile$,
      this.isLoading$,
      hasConsecutiveFollowingMonthPrices$,
      hasRequiredDates$,
    ]).pipe(
      map(
        ([tripType, pricesAvailable, isMobile, isLoading, hasConsecutiveFollowingMonthPrices, hasRequiredDates]) =>
          this.type === PriceCalendarType.PRICES &&
          tripType === TripType.RETURN &&
          pricesAvailable &&
          !isMobile &&
          !isLoading &&
          hasConsecutiveFollowingMonthPrices &&
          !hasRequiredDates
      )
    );

    this.firstDateToShowPriceSearchPopover$ = this.travelDates$.pipe(
      filter((travelDates) => isPresent(travelDates?.departureDate)),
      map((travelDates) => travelDates.departureDate.plusDays(DAYS_MONTH))
    );

    if (this.showTripPriceFrom) {
      this.pricesAvailable$ = this.priceCalendarService.pricesAvailable$;
      this.noPricesAvailableForDates$ = this.priceCalendarService.pricesAvailable$.pipe(
        combineLatestWith(this.priceCalendarService.pricesAvailableForSelectedDates$, hasRequiredDates$),
        map(
          ([pricesAvailable, pricesAvailableForSelectedDates, hasRequiredDates]) =>
            !pricesAvailable || (!pricesAvailableForSelectedDates && hasRequiredDates)
        ),
        startWith(false)
      );
    }

    this.hasDiscountCode$ = this.priceCalendarService.params$.pipe(map((params) => isPresent(params?.discountCode)));

    this.datesSelected$ = combineLatest([this.travelDates$, this.tripType$]).pipe(
      map(
        ([{ departureDate, returnDate }, tripType]) =>
          isPresent(departureDate) && (tripType === TripType.ONEWAY || isPresent(returnDate))
      )
    );

    this.datePickerTitleLabel$ = combineLatest([this.tripType$, this.travelDates$]).pipe(
      map(([tripType, { departureDate }]) => {
        if (tripType === TripType.ONEWAY) {
          return 'bookingWidget.priceCalendar.selectDeparture.oneWay';
        }
        return isPresent(departureDate)
          ? 'bookingWidget.priceCalendar.selectReturn'
          : 'bookingWidget.priceCalendar.selectDeparture.roundTrip';
      })
    );

    this.ctaDisabled$ = combineLatest([this.tripType$, this.travelDates$]).pipe(
      map(([tripType, travelDates]) => {
        if (tripType === TripType.RETURN) {
          return !isPresent(travelDates.departureDate) || !isPresent(travelDates.returnDate);
        }
        return !isPresent(travelDates.departureDate);
      })
    );

    if (this.showSubtitle) {
      this.subTitle$ = this.priceCalendarService.params$.pipe(
        filter(Boolean),
        switchMap(({ origin, destination }) => {
          return forkJoin({
            origin: this.locationRouteCffService.bestGuessFor(origin, this.languageService.localeValue),
            destination: this.locationRouteCffService.bestGuessFor(destination, this.languageService.localeValue),
          });
        }),
        map(({ origin, destination }) => {
          const originCity = origin ? origin.cityName : '';
          const destinationCity = destination ? destination.cityName : '';
          return { origin: originCity, destination: destinationCity };
        })
      );
    }

    if (this.enablePreFlightSearchTracking) {
      this.subscription.add(
        combineLatest([
          this.preFlightSearchTrigger$,
          this.datesSelected$.pipe(
            distinctUntilChanged(),
            filter((datesSelected) => datesSelected)
          ),
        ])
          .pipe(switchMap(() => this.priceCalendarService.getPriceCalendarCTAParams()))
          .subscribe((ctaParams) => {
            const { tripType, paxAmount, travelClass, flights } = ctaParams;

            const departureFlight: FlightSegment = {
              origin: flights[0]?.origin?.locationCode,
              destination: flights[0]?.destination?.locationCode,
              departureDate: flights[0]?.departureDate,
            };

            const returnFlight: FlightSegment = {
              origin: flights[1]?.origin?.locationCode,
              destination: flights[1]?.destination?.locationCode,
              departureDate: flights[1]?.departureDate,
            };

            const flightSegments: FlightSegment[] =
              tripType === TripType.RETURN ? [departureFlight, returnFlight] : [departureFlight];
            if (paxAmount && travelClass) {
              this.gtmService.preFlightSearch({
                flights: flightSegments,
                paxAmount,
                travelClass,
                isAward: this.type === PriceCalendarType.ONLY_DATES,
              });
            }
          })
      );
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  closeModal(): void {
    this.modalClose.emit(false);
  }

  addReturn(): void {
    this.priceCalendarService.convertToReturnTrip();
    this.addReturnClicked.emit();
  }

  updateCalendarDates(calendarDates: [string] | [string, string]): void {
    doLater(() => {
      const newDates = calendarDatesToGlobalBookingTripDates(calendarDates);
      if (shouldUpdateCalendarDates(newDates, this.priceCalendarService.travelDatesValues)) {
        this.priceCalendarService.updateCalendarDates(newDates);
        this.travelDatesChanged.emit(newDates);
        this.preFlightSearchTrigger$.next(true);
      }
    });
  }

  setHistogramMonth(selectedMonthIndex = 0): void {
    this.selectedHistogramMonth$.next(selectedMonthIndex);
  }

  handleHistogramClick(selectedBar: HistogramBar): void {
    if (!selectedBar.noFlight) {
      this.gtmService.trackElement(
        'histogram',
        'flight-search',
        ElementTypes.BOOKING_WIDGET,
        selectedBar?.isCheapest ? 'lowest-month-click' : 'other-month-click',
        ElementActions.CLICK,
        undefined,
        this.type === PriceCalendarType.ONLY_DATES ? SearchType.AWARD : SearchType.BOOKING_FLOW
      );
    }
    this.scrollToHistogramMonth$.next(selectedBar.index);
  }

  handleBarsScrolled(_direction: Direction): void {
    //TODO: Check what this doing ?
    this.gtmService.trackElement(
      'histogram',
      'flight-search',
      ElementTypes.BOOKING_WIDGET,
      'arrow-click',
      ElementActions.CLICK,
      undefined,
      this.type === PriceCalendarType.ONLY_DATES ? SearchType.AWARD : SearchType.BOOKING_FLOW
    );
  }

  emitCTAClicked(): void {
    doLater(() => {
      this.subscription.add(
        this.priceCalendarService
          .getPriceCalendarCTAParams()
          .pipe(take(1))
          .subscribe((ctaParams) => {
            this.ctaClicked.emit({
              ...ctaParams,
              flexibleDates: this.flexibleDates$.value,
            });
          })
      );
    });
  }

  onCancelSelection(): void {
    this.cancelSelection.emit();
    this.startSearchWithSelectedDates(true);
    this.closeModal();
  }

  onClose(): void {
    if (this.enableNewSearchAutomatically) {
      this.onCancelSelection();

      this.gtmService.trackElement(
        'global-widget-cancel-new-search-from-price-calendar',
        GaContext.BOOKING_FLOW,
        ElementTypes.BOOKING_WIDGET,
        ElementTypes.BUTTON,
        '',
        ElementActions.CLICK
      );
    }
  }

  startNewSearch(): void {
    this.startSearch.emit();
    this.modalClose.next(true);

    this.gtmService.trackElement(
      'global-widget-start-new-search-from-price-calendar',
      GaContext.BOOKING_FLOW,
      ElementTypes.BOOKING_WIDGET,
      ElementTypes.BUTTON,
      '',
      ElementActions.CLICK
    );
  }

  startSearchWithSelectedDates(onlyOnce = false): void {
    this.subscription.add(
      this.openWithParams$
        .pipe(
          onlyOnce ? take(1) : map((params) => params),
          filter((params) => isPresent(params) && priceCalendarBaseParamsAreValid(params)),
          distinctUntilChanged(
            (pre: PriceCalendarWithPricesParams, next: PriceCalendarWithPricesParams) =>
              pre.origin === next.origin &&
              pre.destination === next.destination &&
              pre.tripType === next.tripType &&
              pre.travelClass === next.travelClass
          )
        )
        .subscribe((params) => {
          this.selectedHistogramMonth$.next(undefined);
          this.priceCalendarService.updateParams({
            ...params,
            initialTravelDates: {
              ...params.initialTravelDates,
              returnDate: params.tripType !== TripType.RETURN ? undefined : params.initialTravelDates?.returnDate,
            },
          });
        })
    );
  }
}
